import { useState } from 'react';
import { Alert, Button, ColorInput, FileButton, Group, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconUpload } from '@tabler/icons-react';
import { ROKU_BRAND_KIT_COPY as R, TV_BRAND_KIT_COPY as C, TV_TILE_TEXT_MAX, type FactoryAssetPlatform } from '@/lib/constants/appFactory';
import type { FactoryAssetSetCreateReq } from '@/lib/types/AppFactory';
import { BrandKitUtils } from '../BrandKitUtils';

interface TvBrandKitFormProps {
  /** Whose set: the Android family (firetv) or Roku (no tile art, Roku wording). */
  platform?: FactoryAssetPlatform;
  /** The platform row's title — the default app name on the tiles. */
  appTitle?: string;
  disabled?: boolean;
  generating?: boolean;
  onGenerate: (req: FactoryAssetSetCreateReq) => void;
}

/**
 * The Android-family Brand kit inputs (TV graphics v2, design D1): a square
 * symbol (required — Google TV draws the app as a circle), an optional wide
 * wordmark, the app name set in type when there is no wordmark, a ground
 * colour (suggested from the symbol; the Dark/Light presets remain), and
 * optional 16:9 tile art. Client checks are advice only; the factory judges.
 * Since Roku parity (design_roku_parity) the Roku tab takes it too, without
 * the tile art (Roku's poster sits on the ground colour).
 */
export function TvBrandKitForm({ platform = 'firetv', appTitle, disabled, generating, onGenerate }: TvBrandKitFormProps) {
  const roku = platform === 'roku';
  const [symbol, setSymbol] = useState<File | null>(null);
  const [symbolAdvice, setSymbolAdvice] = useState<string[]>([]);
  const [suggestedHex, setSuggestedHex] = useState<string | null>(null);
  const [wordmark, setWordmark] = useState<File | null>(null);
  const [wordmarkAdvice, setWordmarkAdvice] = useState<string[]>([]);
  const [tileArt, setTileArt] = useState<File | null>(null);
  const [tileText, setTileText] = useState((appTitle ?? '').slice(0, TV_TILE_TEXT_MAX));
  const [bgColorHex, setBgColorHex] = useState(BrandKitUtils.PRESETS[0].bgColorHex);
  const [textColorHex, setTextColorHex] = useState(BrandKitUtils.PRESETS[0].textColorHex);
  const [accentColorHex, setAccentColorHex] = useState('');

  const tooBig = (file: File) => {
    if (file.size <= BrandKitUtils.MAX_LOGO_BYTES) return false;
    notifications.show({ title: 'File too large', message: 'Images must be under 8MB.', color: 'red' });
    return true;
  };

  const pickSymbol = async (file: File | null) => {
    if (!file || tooBig(file)) return;
    setSymbol(file);
    const px = await BrandKitUtils.readImagePixels(file);
    setSymbolAdvice(BrandKitUtils.symbolAdvice(px ? { w: px.w, h: px.h } : null, px ? BrandKitUtils.hasTransparency(px.rgba) : null));
    setSuggestedHex(px ? BrandKitUtils.dominantBrandColorHex(px.rgba) : null);
  };

  const pickWordmark = async (file: File | null) => {
    if (!file || tooBig(file)) return;
    setWordmark(file);
    const px = await BrandKitUtils.readImagePixels(file);
    setWordmarkAdvice(px && !BrandKitUtils.hasTransparency(px.rgba) ? [C.noTransparency] : []);
  };

  const pickTileArt = (file: File | null) => {
    if (!file || tooBig(file)) return;
    setTileArt(file);
  };

  const generate = async () => {
    if (!symbol) return;
    try {
      const [symbolBase64, wordmarkBase64, tileArtBase64] = await Promise.all([
        BrandKitUtils.fileToBase64(symbol),
        wordmark ? BrandKitUtils.fileToBase64(wordmark) : Promise.resolve(null),
        tileArt ? BrandKitUtils.fileToBase64(tileArt) : Promise.resolve(null),
      ]);
      onGenerate(
        BrandKitUtils.tvBrandKitRequest({ symbolBase64, wordmarkBase64, tileArtBase64, tileText, bgColorHex, textColorHex, accentColorHex }),
      );
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Could not read the images.',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="sm">
      <Stack gap={4}>
        <Group gap="sm" align="center">
          <FileButton onChange={(f) => void pickSymbol(f)} accept="image/png">
            {(props) => (
              <Button variant="default" leftSection={<IconUpload size={14} />} {...props}>
                {symbol ? symbol.name : `Choose ${C.symbolLabel} (PNG)`}
              </Button>
            )}
          </FileButton>
          <Text size="xs" c="dimmed">
            {roku ? R.symbolWhy : C.symbolWhy}
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          {C.symbolHelp}
        </Text>
        {symbolAdvice.length > 0 && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" py={6}>
            {symbolAdvice.map((a) => (
              <Text size="sm" key={a}>
                {a}
              </Text>
            ))}
          </Alert>
        )}
      </Stack>

      <Stack gap={4}>
        <FileButton onChange={(f) => void pickWordmark(f)} accept="image/png">
          {(props) => (
            <Button variant="default" leftSection={<IconUpload size={14} />} w="fit-content" {...props}>
              {wordmark ? wordmark.name : `Choose ${C.wordmarkLabel}`}
            </Button>
          )}
        </FileButton>
        <Text size="xs" c="dimmed">
          {C.wordmarkHelp}
        </Text>
        {wordmarkAdvice.map((a) => (
          <Text size="xs" c="orange" key={a}>
            {a}
          </Text>
        ))}
      </Stack>

      <TextInput
        label={C.appNameLabel}
        description={roku ? R.appNameHelp : C.appNameHelp}
        value={tileText}
        maxLength={TV_TILE_TEXT_MAX}
        onChange={(e) => setTileText(e.currentTarget.value)}
        maw={420}
      />

      <Group align="flex-end" gap="sm" wrap="wrap">
        <ColorInput label={C.groundLabel} description={roku ? R.groundHelp : C.groundHelp} value={bgColorHex} onChange={setBgColorHex} w={260} />
        <ColorInput label="Text" value={textColorHex} onChange={setTextColorHex} w={150} />
        <ColorInput label="Accent" placeholder="Optional" value={accentColorHex} onChange={setAccentColorHex} w={150} />
      </Group>
      <Group gap="xs">
        {suggestedHex && (
          <Button size="compact-xs" variant="light" onClick={() => setBgColorHex(suggestedHex)} data-testid="suggested-ground">
            {C.suggestedGround} ({suggestedHex})
          </Button>
        )}
        <Text size="xs" c="dimmed">
          Presets:
        </Text>
        {BrandKitUtils.PRESETS.map((p) => (
          <Button
            key={p.key}
            size="compact-xs"
            variant="light"
            onClick={() => {
              setBgColorHex(p.bgColorHex);
              setTextColorHex(p.textColorHex);
            }}
          >
            {p.label}
          </Button>
        ))}
      </Group>

      {!roku && <Stack gap={4}>
        <FileButton onChange={pickTileArt} accept="image/png,image/jpeg">
          {(props) => (
            <Button variant="default" leftSection={<IconUpload size={14} />} w="fit-content" {...props}>
              {tileArt ? tileArt.name : `Choose ${C.tileArtLabel}`}
            </Button>
          )}
        </FileButton>
        <Text size="xs" c="dimmed">
          {C.tileArtHelp}
        </Text>
      </Stack>}

      <Group>
        <Button onClick={() => void generate()} loading={generating} disabled={disabled || !symbol}>
          Generate Brand Kit
        </Button>
        <Text size="xs" c="dimmed">
          You see each image on a TV screen before anything goes live.
        </Text>
      </Group>
    </Stack>
  );
}
